import { Button, HStack, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';

export interface CountedLensOption<Value extends string> {
	value: Value;
	label: string;
	/** Absent where a count would describe a different set from the list beneath it. */
	count?: number;
	tooltip: string;
}

/**
 * On phone-sized screens the counts are the first thing to go when a caller opts in: the lens can
 * share its row with several other controls, and the labels matter more than the tallies.
 */
const NARROW_VIEWPORT_QUERY = '@media (max-width: 479px)';
const HIDE_COUNT_WHEN_NARROW_CSS = { [NARROW_VIEWPORT_QUERY]: { display: 'none' } } as const;

interface CountedLensFilterProps<Value extends string> {
	/**
	 * What the lens is asking, for the group it wraps these options in.
	 *
	 * Required because the options only mean something together: "All" answers a question the reader
	 * cannot hear otherwise, and a group with no accessible name is a group announced as nothing.
	 */
	label: string;
	value: Value;
	options: readonly CountedLensOption<Value>[];
	onChange: (value: Value) => void;
	stretchOnMobile?: boolean;
	/** For lenses sharing a crowded control row — drops the counts rather than overflowing it. */
	hideCountsWhenNarrow?: boolean;
	/** Draw each choice as its own control instead of placing them in one segmented container. */
	presentation?: 'segmented' | 'buttons';
}

const CountedLensFilter = <Value extends string>(props: CountedLensFilterProps<Value>) => {
	const {
		label,
		value,
		options,
		onChange,
		stretchOnMobile = false,
		hideCountsWhenNarrow = false,
		presentation = 'segmented',
	} = props;
	const responsiveFlex = stretchOnMobile ? { base: 1, md: 'initial' } : undefined;
	const countCss = hideCountsWhenNarrow ? HIDE_COUNT_WHEN_NARROW_CSS : undefined;
	const separateButtons = presentation === 'buttons';

	return (
		// The strip carries "pick one of these" visually; `role="group"` is what says it to a reader
		// who cannot see it. A bare `aria-label` on a div without a role is ignored.
		<HStack
			layerStyle={separateButtons ? undefined : 'control'}
			h="40px"
			gap={separateButtons ? 2 : 1}
			p={separateButtons ? 0 : 1}
			w="auto"
			flex={responsiveFlex}
			flexShrink={0}
			role="group"
			aria-label={label}
			data-lens-presentation={presentation}
		>
			{options.map((option) => {
				const isActive = value === option.value;
				return (
					<Tooltip key={option.value} content={option.tooltip} openDelay={300}>
						<Button
							size="sm"
							h={separateButtons ? 10 : '30px'}
							flex={responsiveFlex}
							px={separateButtons ? 4 : 3}
							borderRadius="full"
							variant="ghost"
							layerStyle={separateButtons ? 'control' : undefined}
							fontSize="sm"
							fontWeight={isActive ? 'semibold' : 'medium'}
							color={isActive ? 'brand.contrast' : 'brand.fg'}
							bg={isActive ? 'brand.solid' : separateButtons ? 'surface.default' : 'transparent'}
							_hover={{ bg: isActive ? 'brand.solid' : 'surface.subtle' }}
							// Selection was carried by background, weight and colour alone, so nothing
							// announced which lens the list below belongs to.
							aria-pressed={isActive}
							onClick={() => onChange(option.value)}
						>
							{option.label}
							{option.count !== undefined && (
								<Text
									as="span"
									fontSize="xs"
									color={isActive ? 'brand.contrast' : 'fg.subtle'}
									textAlign="right"
									fontVariantNumeric="tabular-nums"
									flexShrink={0}
									css={countCss}
								>
									{option.count}
								</Text>
							)}
						</Button>
					</Tooltip>
				);
			})}
		</HStack>
	);
};

export default CountedLensFilter;
